import { RefObject, useEffect, useState } from 'react';

/*  This hook takes a list of element ids, finds the top position of each element relative
    to the window and compares it to the top position of the element referenced by `refElement`.
    The hook then filters out all element ids where the element top position is higher than
    the refElementBoundary of `refElement`, and takes the element with the max top.  This is equivalent
    to taking the element that is below and closest to `refElement`.  A boolean function
    is then returned - this will evaluate to true for that element, and false for all others.
*/
export const useActiveSection = (
  ids: string[],
  refElement: RefObject<HTMLDivElement>,
  refElementBoundary: 'top' | 'bottom'
): string => {
  const [activeSectionId, setActiveSectionId] = useState(ids[0]);

  useEffect(() => {
    const setActiveSectionOnScroll = (): void => {
      const refElementBoundaryPosition =
        refElementBoundary === 'top'
          ? refElement?.current?.getBoundingClientRect()?.top
          : refElement?.current?.getBoundingClientRect()?.bottom;
      if (!refElementBoundaryPosition) {
        return;
      }

      const activeId = ids
        .map((id) => ({
          id,
          top: document.getElementById(id)?.getBoundingClientRect()?.top,
        }))
        // We include a small grace window of 5px to ensure that the new active section is registered.  This is
        // necessary due to the scrolling being slightly inaccurate
        .filter(({ top }) => top != null && top <= refElementBoundaryPosition + 5)
        // Sort by maximum top value
        .sort(({ top: a }, { top: b }) => (b ?? 0) - (a ?? 0))[0]?.id;

      // It is possible that there are no sections below the nav, in this case we keep
      // the previous value.
      if (activeId) {
        setActiveSectionId(activeId);
      }
    };
    setActiveSectionOnScroll();
    window.addEventListener('scroll', setActiveSectionOnScroll);
    return () => window.removeEventListener('scroll', setActiveSectionOnScroll);
  });

  return activeSectionId;
};
